import {Component, forwardRef, Input} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';

import {UiBadgeComponent, UiInputComponent} from '../../shared/ui';

@Component({
  selector: 'app-tags-input',
  standalone: true,
  imports: [
    UiBadgeComponent,
    UiInputComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagsInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TagsInputComponent),
      multi: true,
    }
  ],
  templateUrl: './tags-input.component.html',
  styleUrl: './tags-input.component.scss'
})
export class TagsInputComponent implements ControlValueAccessor, Validator {

  @Input() placeholder = 'Add and press Enter';
  @Input() variant: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error' | 'neutral' = 'primary';

  /**
   * If true, the control is considered invalid while the user has typed text
   * that hasn't been committed as a tag yet.
   */
  @Input() requireCommit = true;

  value: string[] = [];
  disabled = false;
  newTag = '';

  private onChange: (val: string[]) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  private onValidatorChange: () => void = () => {
  };

  private wasBlurred = false;
  private isFocused = false;

  get hasPendingInput(): boolean {
    return this.requireCommit && !!this.newTag.trim();
  }

  get showPendingAsError(): boolean {
    return this.hasPendingInput && this.wasBlurred;
  }

  get showPendingAsWarning(): boolean {
    // show warning when there's pending input, user is not currently focused,
    // but hasn't blurred yet (or blurred but we prefer error styling then)
    return this.hasPendingInput && !this.isFocused && !this.wasBlurred;
  }

  get showPendingHint(): boolean {
    // only show a hint message after the user has left the field at least once
    return this.showPendingAsError;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.requireCommit) {
      return null;
    }
    const pending = this.newTag.trim();
    return pending ? {pendingTag: true} : null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  writeValue(val: string[] | null): void {
    this.value = Array.isArray(val) ? [...val] : [];
    // writing a value doesn't clear draft input by default; keep current behavior
    this.onValidatorChange();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.onValidatorChange();
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
    this.wasBlurred = true;
    this.onTouched();
    this.onValidatorChange();
  }

  setNewTag(val: string): void {
    this.newTag = val;
    this.onValidatorChange();
  }

  addTag(raw?: string) {
    if (this.disabled) {
      this.newTag = '';
      this.onValidatorChange();
      return;
    }

    const v = (raw ?? this.newTag).trim();
    if (!v) {
      this.newTag = '';
      this.onValidatorChange();
      return;
    }

    const exists = this.value.some(x => x.toLowerCase() === v.toLowerCase());
    if (!exists) {
      this.value = [...this.value, v];
      this.onChange(this.value);
    }
    this.newTag = '';
    // once committed, reset blur state so we don't keep showing an error
    this.wasBlurred = false;
    this.onValidatorChange();
  }

  removeTag(tag: string) {
    if (this.disabled) {
      return;
    }
    this.value = this.value.filter(t => t !== tag);
    this.onChange(this.value);
  }

  handleKeyDown(e: KeyboardEvent) {
    if (this.disabled) {
      return;
    }

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      this.addTag();
      this.onTouched();
    } else if (e.key === 'Tab') {
      if (this.newTag.trim()) {
        e.preventDefault();
        this.addTag();
        this.onTouched();
      }
    }
  }

  handlePaste(e: ClipboardEvent) {
    if (this.disabled) {
      return;
    }

    const text = e.clipboardData?.getData('text') ?? '';
    if (text.includes(',') || text.includes('\n')) {
      e.preventDefault();
      text
        .split(/[\n,]/)
        .map(s => s.trim())
        .filter(Boolean)
        .forEach(s => this.addTag(s));
      this.onTouched();
      this.onValidatorChange();
    }
  }

}
